import styled, { css, createGlobalStyle } from "styled-components/macro";
import {
  brandPrimary70,
  darkBlue,
  darkModeBackgroundHoveredColor,
  darkModeDarkTextColor,
  darkModeModalBackgroundColor,
  darkModeTitleBackgroundColor,
  flDangerBorder,
  lightBlueActive,
  lightBorder3,
  lightNeutral10,
  lightTextBody,
  lightTextOnDark,
} from "../../../constants/colors";
import {
  BREAKPOINT_DESKTOP,
  BREAKPOINT_MOBILE,
  BREAKPOINT_TABLET,
  Z_INDEX,
} from "constants/constants";
import { BREAKPOINT_DATERANGEPICKER_TWO_ROWS } from "./dateRangeUtils";
const BREAKPOINT_IPHONE_XS = 376;
/** below which, the control buttons can only fit below, not beside*/

export const DateRangePickerStyles = styled.div<{
  /** what step of datepicking are we on */
  $step: "1" | "2" | null;
  $scale?: number;
  $isClockOpen: boolean;
  $isDarkMode: boolean;
  $isVerticalLayout: boolean;
}>`
  /* prevent unnecessary re-renders when calendar should be hidden */
  .date-range-picker-calendar {
    ${props => (props.$step ? "" : "display: none;")}
  }
  z-index: ${Z_INDEX.L7};
  display: flex;
  align-items: center;
  justify-content: center;
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    justify-content: flex-end;
    margin-right: 16px;
  }
  .MuiPickersToolbar-root {
    .MuiTypography-root.MuiTypography-overline {
      display: none;
    }
    .MuiPickersToolbar-content {
      justify-content: center;
    }
  }
  .helper-text {
    position: absolute;
    bottom: -5px;
    font-size: 12px;
    right: 0;
    left: -36px;
    margin: auto;
    width: fit-content;
    color: ${flDangerBorder};
    background: white;
    @media (min-width: ${BREAKPOINT_DESKTOP}px) {
      bottom: -12px;
    }
    @media (max-width: ${BREAKPOINT_DATERANGEPICKER_TWO_ROWS}px) {
      left: unset;
      bottom: 6px;
    }
  }
  .picker-and-controls {
    margin-left: 8px;
    display: flex;
    flex-direction: column;
    .daterange-picker-controls {
      transform: translateY(3px);
      padding-left: 6px;
      display: flex;
      gap: 4px;
      align-items: center;
      justify-content: flex-start;
    }
  }
  .duration-indicator {
    white-space: nowrap;
    position: absolute;
    opacity: 0.9;
    bottom: -18px;
    font-size: 12px;
    display: flex;
    justify-content: flex-end;
    width: 100%;
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      width: fit-content;
      margin-right: 14px;
      margin-left: auto;
    }
    left: 0;
    right: 0;
    margin: auto;
    transition: opacity 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
    transition-delay: 0.1s;
    &.hidden {
      transition: opacity 0.06s cubic-bezier(0.075, 0.82, 0.165, 1);
      transition-delay: 0s;
      opacity: 0;
      pointer-events: none;
    }
  }

  .date-range-picker {
    width: fit-content;
    @media (min-width: ${BREAKPOINT_TABLET}px) {
    }
  }
  .react-datetimerange-picker {
    margin-left: 6px;
    @media (min-width: ${BREAKPOINT_TABLET}px) {
      margin-left: 12px;
    }
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      width: 100%;
    }
  }
  .react-datetimerange-picker__wrapper,
  .react-date-picker__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px 0;
    justify-content: center;
    width: fit-content;
    flex-wrap: nowrap;
    @media (min-width: ${BREAKPOINT_DATERANGEPICKER_TWO_ROWS}px) {
      justify-content: center;
      gap: 8px;
      .react-datetimerange-picker__range-divider,
      .react-date-picker__range-divider {
        margin-left: 8px;
        margin-right: 8px;
      }
    }
    @media (min-width: ${BREAKPOINT_DESKTOP}px) {
      align-items: center;
    }
  }

  .react-datetimerange-picker__inputGroup,
  .react-date-picker__inputGroup {
    border: none !important;
    border-radius: 0 !important;
    border-bottom: 2px solid ${darkBlue} !important;
    align-items: center !important;

    /* hide the spinners/spin buttons for the inputs (all browsers) */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
    /* hide the month select dropdown chevron (all browsers) */
    .react-datetimerange-picker__inputGroup__month,
    .react-date-picker__inputGroup__month {
      &::-ms-expand {
        display: none;
      }

      ${`-webkit-appearance: none;
      -moz-appearance: none;`}

      text-indent: 1px;
      text-overflow: "";
    }
    /* selecting start and end states */
    &:after {
      position: absolute;
      font-weight: bold;
      top: 0px;
      @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
        top: 4px;
      }
      left: -4px;
      right: 0;
      ${props =>
        props.$isClockOpen &&
        css`
          width: fit-content;
          margin-left: auto;
        `}
      font-size: 12px;
      color: ${brandPrimary70};
      padding: 0 4px;
      transform: translateY(-100%);
    }
    &.react-datetimerange-picker__inputGroup:first-child,
    &.react-datetimerange-picker__inputGroup:not(:first-child) {
      position: relative;
      /* date inputs need to scooch down to show their labels */
      transform: translateY(-2px);
      @media (max-width: ${BREAKPOINT_DATERANGEPICKER_TWO_ROWS}px) {
        transform: translateY(8px);
      }
      &:after {
        color: ${darkBlue};
      }
    }
    &.react-datetimerange-picker__inputGroup:first-child {
      &:after {
        content: "Start time";
      }
    }
    &.react-datetimerange-picker__inputGroup:not(:first-child) {
      &:after {
        content: "End time";
      }
    }

    ${({ $step, $isClockOpen, $isVerticalLayout }) =>
      ($step === "1" || ($isClockOpen && !$isVerticalLayout)) &&
      css`
        /* selecting start datetime, so highlight border & add :after indicator saying "select start time" */
        &.react-datetimerange-picker__inputGroup:first-child {
          border-bottom: 3px solid ${brandPrimary70} !important;
          margin-bottom: -1px;
          &:after {
            color: ${brandPrimary70};
            content: "Select start ${$isClockOpen ? "time" : "date"}";
          }
        }
      `}
    ${({ $step, $isClockOpen, $isVerticalLayout }) =>
      ($step === "2" || ($isClockOpen && !$isVerticalLayout)) &&
      css`
        /* selecting start datetime, so highlight border & add :after indicator saying "select start time" */
        &.react-datetimerange-picker__inputGroup:not(:first-child) {
          border-bottom: 3px solid ${brandPrimary70} !important;
          margin-bottom: -1px;
          &:after {
            color: ${brandPrimary70};
            content: "Select end ${$isClockOpen ? "time" : "date"}";
          }
        }
      `}
    padding: 4px 0px 4px 2px;
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      padding: 2px;
      padding-bottom: 4px;
      width: fit-content;
    }
    display: flex;
    height: fit-content;
    align-items: baseline;
    background: ${props =>
      props.$isDarkMode ? darkModeModalBackgroundColor : "white"};
    border-radius: 4px;
    border: 1px solid ${lightBorder3};
  }

  .react-datetimerange-picker__inputGroup__input,
  .react-datetimerange-picker__inputGroup__leadingZero,
  .react-date-picker__inputGroup__input,
  .react-date-picker__inputGroup__leadingZero {
    width: 0.9ch;
    border: none;
    font-size: 16px;

    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      font-size: 14px;
    }
    text-align: left;
  }
  .react-datetimerange-picker__inputGroup__input {
    height: 22px !important;
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      height: 20px !important;
    }
  }
  .react-datetimerange-picker__inputGroup__leadingZero,
  .react-date-picker__inputGroup__leadingZero {
    height: 0 !important;
    transform: translateY(-1px);
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      font-size: 16px;
    }
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      font-size: 14px;
    }
  }

  .react-datetimerange-picker__inputGroup__leadingZero,
  .react-date-picker__inputGroup__leadingZero {
    height: 0 !important;
    align-self: center;
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      transform: translateY(0px);
    }
    /* firefox */
    @-moz-document url-prefix() {
      transform: translateY(1px);
    }
    /* firefox */
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      @-moz-document url-prefix() {
        transform: translateY(0px);
      }
    }
  }
  .react-datetimerange-picker__inputGroup__input--hasLeadingZero,
  .react-date-picker__inputGroup__input--hasLeadingZero {
    width: calc(1ch + 1px) !important;
    padding-left: 0;
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      padding-left: 0;
    }
  }
  .react-datetimerange-picker__inputGroup__leadingZero,
  .react-date-picker__inputGroup__divider {
    background: white;
    z-index: 1;
    pointer-events: none;
    display: flex;
    align-items: center;
    @supports (-webkit-touch-callout: none) {
      /* CSS specific to iOS devices */
      font-family: "Open Sans", sans-serif;
      transform: translateY(-1px) !important;
    }
  }

  .react-date-picker__inputGroup__divider,
  .react-datetime-picker__inputGroup__divider {
    height: 0px !important;
    align-self: baseline;
    background: white;
    z-index: 1;
    font-size: 20px;
    /* firefox */
    @-moz-document url-prefix() {
      transform: translateY(1px);
      padding-left: 1px;
      padding-right: 1px;
    }
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      font-size: 16px !important;
      transform: translateY(4px);
      padding-right: 1px;
      padding-left: 1px;
    }
    /* firefox */
    @-moz-document url-prefix() {
      transform: translateY(1px);
    }
    /* iOS devices */
    @supports (-webkit-touch-callout: none) {
      transform: translateY(6px) !important;
    }
    /* firefox */
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      @-moz-document url-prefix() {
        transform: translateY(5px);
      }
    }
  }

  @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
    .react-datetimerange-picker__inputGroup__hour {
      padding-left: 0;
    }
  }
  /* selects the ":" between 00:00 */
  .react-datetimerange-picker__inputGroup__hour
    + .react-datetime-picker__inputGroup__divider {
    height: 0px !important;
    font-size: 15px !important;
    transform: translateY(-2px);
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      transform: translateY(2px);
    }
    @supports (-webkit-touch-callout: none) {
      /* CSS specific to iOS devices */
      transform: translateY(4px) !important;
    }
  }

  .react-datetimerange-picker__button,
  .react-date-picker__button {
    &:hover {
      background: ${lightBlueActive} !important;
    }
  }
  .react-datetimerange-picker__calendar-button,
  .react-date-picker__calendar-button {
    align-self: center;
  }
  .react-datetimerange-picker__inputGroup__year,
  .react-date-picker__inputGroup__year {
    width: 4.2ch !important;
    margin-right: 4px;
    padding-left: 0;
    margin-left: 1px;
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      width: 4ch !important;
    }
  }
  .react-date-picker__inputGroup__year {
    margin-right: 1px;
  }

  .react-datetimerange-picker__inputGroup__month,
  .react-date-picker__inputGroup__month {
    width: 2ch !important;
    text-align: center;
    @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
      padding-right: 2px;
    }
  }
  .react-datetimerange-picker__inputGroup__day,
  .react-date-picker__inputGroup__day {
    width: 2ch !important;
    text-align: center;
    padding-left: 0;
    padding-right: 0;
    margin-right: 1px;
    margin-left: 1px;
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      width: 2ch !important;
    }
  }
  .react-datetimerange-picker__inputGroup__input--hasLeadingZero,
  .react-date-picker__inputGroup__input--hasLeadingZero {
    width: calc(1ch + 1px) !important;
    padding-left: calc(1ch + 1px);
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      padding-left: calc(1ch + 2px);
    }

    margin-left: -1ch;
    &.react-datetimerange-picker__inputGroup__day,
    &.rdate-time-datetimerange-picker__inputGroup__day {
      padding-left: calc(1ch + 1px);
    }
    &.react-datetimerange-picker__inputGroup__month,
    &.rdate-time-datetimerange-picker__inputGroup__month {
      margin-left: calc(-1ch - 1px);
    }
  }
  .react-datetimerange-picker__inputGroup__hour,
  .react-date-picker__inputGroup__hour {
    width: 2ch !important;
    padding-right: 0;
    &.react-datetimerange-picker__inputGroup__input--hasLeadingZero,
    &.rdate-time-datetimerange-picker__inputGroup__input--hasLeadingZero {
      width: 1.16ch !important;
    }
  }
  .react-datetimerange-picker__inputGroup__minute,
  .react-date-picker__inputGroup__minute {
    width: 2ch !important;
    &:not(.react-datetimerange-picker__inputGroup__input--hasLeadingZero) {
      padding-left: 0;
    }
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      width: 2ch !important;
    }
    &.react-datetimerange-picker__inputGroup__input--hasLeadingZero,
    &.rdate-time-datetimerange-picker__inputGroup__input--hasLeadingZero {
      width: 1.16ch !important;
    }
  }
  position: relative;
  .react-datetimerange-picker__calendar,
  .react-date-picker__calendar {
    position: absolute;
    top: 100% !important;
    left: 0 !important;
    right: 0 !important;
    justify-content: center;
    opacity: 1;
    pointer-events: all;
    transition: opacity 0.2s cubic-bezier(0.075, 0.82, 0.165, 1);
  }
  .react-datetimerange-picker__calendar--closed,
  .react-date-picker__calendar--closed {
    opacity: 0;
    pointer-events: none;
    .react-calendar {
      pointer-events: none;
    }
    transition: opacity 0.4s cubic-bezier(0.075, 0.82, 0.165, 1) 0.16s;
  }

  .react-datetimerange-picker__calendar--open,
  .react-date-picker__calendar--open {
    pointer-events: none;
  }
  .react-calendar {
    z-index: 1000000;
    position: absolute;
    pointer-events: auto;
    transform: scale(${props => props.$scale});
    transform-origin: top right;
    border: 1px solid ${lightBorder3};
    border-radius: 8px;
    margin: auto;
    @media (max-width: ${BREAKPOINT_MOBILE}px) {
      margin: 42px auto;
    }
    padding: 24px;
    box-shadow: 0px 4px 8px -2px #0000001a;
    .react-calendar__navigation__next2-button,
    .react-calendar__navigation__prev2-button {
      display: none;
    }
    .react-calendar__tile.react-calendar__tile--now {
      border: 1px dashed ${brandPrimary70};
      border-radius: 4px;
    }
    button.react-calendar__navigation__arrow:disabled {
      opacity: 0.4;
    }
    .react-calendar__tile {
      width: 44px;
      height: 36px;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: none;
      font-size: 12px;
      @media (min-width: ${BREAKPOINT_MOBILE}px) {
        font-size: 14px;
      }
      border-radius: 4px;
      font-weight: normal;
      color: ${lightTextBody};
      &:hover {
        background: #e6e6e6;
      }
    }
    .react-calendar__navigation {
      margin-bottom: 20px;
      height: 28px;
    }
    .react-calendar__navigation__arrow {
      padding: 0;
      height: 28px;
      width: 28px;
      min-width: 0;
      font-size: 20px;
      background: none;
      .MuiSvgIcon-root {
        color: ${brandPrimary70};
        width: 28px;
        height: 28px;
      }
    }
    .react-calendar__navigation__label {
      padding: 0;
      height: 28px;
      background: none;
      font-size: 14px;
      font-family: "Open Sans", sans-serif;
    }
    .react-calendar__month-view__weekdays {
      margin-bottom: 12px;
      height: 28px;
    }
    .react-calendar__month-view__weekdays__weekday {
      background: ${lightNeutral10};
      color: ${brandPrimary70};
      &:first-child {
        border-radius: 6px 0 0 0;
      }
      &:last-child {
        border-radius: 0 6px 0 0;
      }
      abbr {
        text-decoration: none;
        font-weight: normal;
      }
    }
    .react-calendar__tile--active {
      color: ${lightTextOnDark};
      background: ${brandPrimary70};
    }
    .react-calendar__tile--range {
      background: ${lightNeutral10};
      color: ${brandPrimary70};
    }
    .react-calendar__tile--rangeStart,
    .react-calendar__tile--rangeEnd {
      color: ${lightTextOnDark};
      background: ${brandPrimary70};
    }
    button:disabled {
      pointer-events: none;
    }
    .react-calendar__month-view__days__day:disabled {
      opacity: 0.2;
    }
    .react-calendar__month-view__days__day--neighboringMonth {
      visibility: hidden;
    }
    .react-calendar__tile--hasActive.react-calendar__year-view__months__month,
    .react-calendar__tile--hasActive.react-calendar__decade-view__years__year {
      color: ${lightTextOnDark};
      background: ${brandPrimary70};
    }
    .react-calendar__decade-view__years__year:disabled,
    .react-calendar__year-view__months__month:disabled {
      opacity: 0.5;
    }
    ${props =>
      props.$isDarkMode
        ? css`
            background: ${darkModeModalBackgroundColor};
            .react-calendar__month-view__weekdays__weekday {
              background: ${darkModeTitleBackgroundColor};
            }
            .react-calendar__tile.react-calendar__month-view__days__day:not(
                .react-calendar__tile--active
              ) {
              color: ${darkModeDarkTextColor};
            }
            .react-calendar__tile--active {
              background: ${brandPrimary70};
            }
            .react-calendar__tile:hover {
              background: ${darkModeBackgroundHoveredColor};
            }
          `
        : ""}
  }
  &&& {
    .fadeOut {
      opacity: 0.5;
    }
    .btnForward,
    .btnBack {
      width: 20px;
      height: 28px;
      @media (max-width: ${396}px) {
        display: none;
      }
      @media (min-width: ${BREAKPOINT_TABLET}px) {
        width: 30px;
        height: 30px;
        padding: 0;
        margin-bottom: -5px;
      }
      &:hover {
        background: ${lightBlueActive};
      }
    }
    .btnBack {
      transform: rotate(180deg);
    }
    .btnForward {
      transform: none;
    }
    .forwardIcon,
    .backIcon {
      fill: ${darkBlue};
      width: 28px;
      height: 32px;
      @media (min-width: ${BREAKPOINT_TABLET}px) {
        width: 36px;
        height: 36px;
      }
    }
  }
  @media (max-width: ${BREAKPOINT_IPHONE_XS}px) {
    .picker-and-controls {
      width: 100%;
    }
    .react-datetimerange-picker__wrapper {
      width: 100%;
      justify-content: space-evenly;
    }
    .react-datetimerange-picker {
      margin: 0;
    }
  }
`;
export const DateRangePickerGlobalStyles = createGlobalStyle<{
  /** what step of datepicking are we on */
  $step: "1" | "2" | null;
  $scale?: number;
  $isClockOpen: boolean;
  $isDarkMode: boolean;
  $isVerticalLayout: boolean;
}>`
  .time-pickers-popper{
    z-index: ${Z_INDEX.L9}
  }
  .time-pickers-wrapper {
    display: flex;
    gap: 12px;
    opacity: 1;
    transition: opacity 0.4s cubic-bezier(0.075, 0.82, 0.165, 1);
    &.hidden {
      opacity: 0;
      &,
      * {
        pointer-events: none;
      }
    }
    .MuiPickersLayout-root {
      border-radius: 5px;
      box-shadow: 0px 2px 8px #00000040;
      border: 1px solid ${lightBlueActive};
    }
    @media (min-width: ${BREAKPOINT_TABLET}px) {
      .start-time-picker .MuiPickersLayout-actionBar {
        display: none;
      }
    }
    @media (max-width: ${BREAKPOINT_TABLET}px) {
      .start-time-picker,
      .end-time-picker {
        opacity: 0;
        position: absolute;
        transition: all 0.4s cubic-bezier(0.075, 0.82, 0.165, 1);
      }
      .end-time-picker {
        transform: translateX(32px);
        right: -33vw;
      }
      &.step-1 {
        .start-time-picker {
          opacity: 1;
        }
        .end-time-picker {
          transform: translateX(128px);
          opacity: 0;
          &,
          * {
            pointer-events: none;
          }
        }
      }
      .start-time-picker {
        transform: translateX(-32px);
        left: -33vw;
      }
      &.step-2 {
        .end-time-picker {
          opacity: 1;
        }
        .start-time-picker {
          transform: translateX(-128px);
          opacity: 0;
          &,
          * {
            pointer-events: none;
          }
        }
      }
    }
  }
`;
